import { buildPaginatedRequest, PaginatedRequestParams, PaginatedResponse } from "../../../../../common/pagination";
import { BulkResponse, HistoryTrackingItem, OrderRepository, ReportUrl } from "../../data";
import { CheckDeliverDTO, OrderDto, OrderItemDto } from "../dtos";
import { WindowUtils } from "../../../../../helpers/window";
import { DeliveryPartnerDto } from "../dtos/delivery-partner";
import { ConfirmReadyDto, ReadyToDeliveryDto } from "../dtos/ready-to-delivery";
import { FileService, TawreedFile } from "../../../../upload";
import { TransactionDocumentFilesDTO } from "../../../../finance/domain/dtos/transaction-document-file";
import { OrderFilesDTO } from "../dtos/order-documents";
import { ConfirmAllDto } from "../dtos/actions/confirm-all";
import { OrderDeliveryPartners } from "../dtos/actions/orders-delivery-partners";

class OrderService {

    private readonly repository: OrderRepository;
    private filesService = new FileService();

    constructor() {
        this.repository = new OrderRepository();
    }

    /**
     * Search
     * @param arg
     * @param returned
     * @returns
     */
    public search(arg: PaginatedRequestParams | string, returned?: boolean): Promise<PaginatedResponse<OrderDto>> {
        const request = typeof arg === 'string' ?
            buildPaginatedRequest({
                sort: { sortOrder: 1, sortField: 'partnerName', multiSortMeta: undefined },
                filters: {
                    orderId: {
                        operator: 'AND',
                        constraints: [
                            {
                                value: arg,
                                matchMode: "contains",
                            },
                        ],
                    },
                }
            })
            :
            buildPaginatedRequest({ ...arg, filters: { ...arg.filters } });
        return this.repository.search(request, returned);
    }

    /**
     * Details
     * @param id
     * @param returned
     * @returns
     */
    public getDetails(id: number, returned?: boolean): Promise<OrderDto> {
        var s = this.repository.getDetails(id, returned)
        console.log(s);
        return s;
    };

    /**
     * Confirm
     * @param orderId
     * @param carrierId
     * @returns {Promise<boolean>}
     */
    public confirm(orderId: number, carrierId: number): Promise<OrderDto> {
        return this.repository.confirm(orderId, carrierId);
    };
    public confirmAll(confirmAllDto:ConfirmAllDto[]): Promise<BulkResponse[]> {
        return this.repository.confirmAll(confirmAllDto);
    };
    /**
     * Review
     * @param orderId
     * @returns {Promise<boolean>}
     */
    public review(orderId: number): Promise<OrderDto> {
        return this.repository.review(orderId);
    };
    public reviewAll(orderIds: number[]): Promise<BulkResponse[]> {
        return this.repository.reviewAll(orderIds);
    };
    public applyDiscount(orderId: number, type: string, value: number): Promise<OrderDto> {
        return this.repository.applyDiscount(orderId, type, value);
    };

    /**
     * Change Carrier
     * @param orderId
     * @param carrierId
     * @returns {Promise<boolean>}
     */
    public changeCarrier(orderId: number, carrierId: number): Promise<OrderDto> {
        return this.repository.changeCarrier(orderId, carrierId);
    };

    /**
     * Cancel
     * @param id
     * @param cancelReasonId
     * @returns
     */
    public cancel(id: number, reasonCode: number, reasonAr: string | undefined,
        reasonEn: string | undefined): Promise<OrderDto> {
        return this.repository.cancel(id, reasonCode, reasonAr, reasonEn);
    };


    /**
     * Cancel
     * @param id
     * @returns
     */
    public cancelReturn(id: number): Promise<OrderDto> {
        return this.repository.cancelReturn(id);
    };

    /**
     * Ship
     * @param id
     * @returns
     */
    public ship(id: number): Promise<OrderDto> {
        return this.repository.ship(id);
    };
    /**
    * ready
    * @param orderId
    * @param carrierId
    * @returns
    */
    public ready(readyToDeliveryDto: ReadyToDeliveryDto): Promise<OrderDto> {
        return this.repository.ready(readyToDeliveryDto);
    };

    public confirmAndReady(readyToDeliveryDto: ConfirmReadyDto): Promise<OrderDto> {
        return this.repository.confirmAndReady(readyToDeliveryDto);
    };

    
    public confirmReadyAndShip(readyToDeliveryDto: ConfirmReadyDto): Promise<OrderDto> {
        return this.repository.confirmReadyAndShip(readyToDeliveryDto);
    };
    public readyAndShip(readyToDeliveryDto: ConfirmReadyDto): Promise<OrderDto> {
        return this.repository.readyAndShip(readyToDeliveryDto);
    };

    /**
     * Deliver
     * @param id
     * @returns
     */
    public async deliver(data: any): Promise<OrderDto> {
        return this.repository.deliver(data)
    };
    public async confirmReadyShipAndDeliver(data: any): Promise<OrderDto>{
        return this.repository.confirmReadyShipAndDeliver(data)
    }
    public async readyShipAndDeliver(data: any): Promise<OrderDto>{
        return this.repository.readyShipAndDeliver(data)
    }
    public async shipAndDeliver(data: any): Promise<OrderDto>{
        return this.repository.shipAndDeliver(data)
    }
    public async addNote(data: any): Promise<OrderDto> {
        return this.repository.addNote(data);
    }
    public async checkDeliver(orderId:number): Promise<CheckDeliverDTO>{
        return this.repository.checkDeliver(orderId);
    }
    /**
     *
     * @param returned
     * @param id
     */
    public return(returned: boolean | undefined, id: number, reasonCode: number, reasonAr:string | undefined,
        reasonEn: string | undefined): Promise<OrderDto> {
        return this.repository.return(returned, id, reasonCode, reasonAr, reasonEn);
    };

    /**
     * Print
     */
    public print(reportUrl: string): Promise<boolean> {
        return new Promise((resolve, reject) => {
            try {
                WindowUtils.print(reportUrl);
                resolve(true);
            } catch {
                reject();
            }
        });
    };

    public updateItem(item: OrderItemDto): Promise<OrderDto> {
        return this.repository.updateItem(item);
    };

    public getDeliveryPartners(salesOrderId: number): Promise<DeliveryPartnerDto[]> {
        return this.repository.getDeliveryPartners(salesOrderId);
    };
    public getDeliveryPartnersForOrders(salesOrderIds: number[]): Promise<OrderDeliveryPartners[]> {
        return this.repository.getDeliveryPartnersForOrders(salesOrderIds);
    };
    public async createFile(file: TawreedFile, title: string, orderId: number, type: string): Promise<boolean> {
        const key = await this.filesService.upload(file).then((res) => res);
        var data: OrderFilesDTO = { orderId: orderId, files: [{ fileId: key, title: title, type: type }] }
        return this.repository.uploadOrderFile(data);

    }
    public async searchItems(id: number, arg: PaginatedRequestParams): Promise<PaginatedResponse<OrderItemDto>> {
        return this.repository.searchItems(id,arg);//.then(res => CustomerCartMapper.toDto(res));
    }

    public async getOrderHistory(arg: PaginatedRequestParams): Promise<PaginatedResponse<HistoryTrackingItem>> {
        return this.repository.getOrderHistory(arg);
    }

    public receive(orderId: number, otp: string): Promise<boolean> {
        return this.repository.receive(orderId, otp);
    };
    public getSalesOrdersURL():Promise<ReportUrl>{
        return this.repository.getSalesOrdersURL();
    }
}


export const orderService = new OrderService();
